import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const MeasurementTools = ({ 
  measurements, 
  onAddMeasurement, 
  onClearMeasurements, 
  isVisible, 
  onToggle,
  activeTool: propActiveTool,
  setActiveTool: propSetActiveTool,
  selectedMeasurementId,
  onSelectMeasurement,
  unit: propUnit,
  setUnit: propSetUnit
}) => {
  const [localActiveTool, setLocalActiveTool] = useState(null);
  const activeTool = propActiveTool !== undefined ? propActiveTool : localActiveTool;
  const setActiveTool = propSetActiveTool !== undefined ? propSetActiveTool : setLocalActiveTool;

  const [localUnit, setLocalUnit] = useState('mm');
  const unit = propUnit !== undefined ? propUnit : localUnit;
  const setUnit = propSetUnit !== undefined ? propSetUnit : setLocalUnit;

  const [measurementMode, setMeasurementMode] = useState('point-to-point');
  const [snapToVertex, setSnapToVertex] = useState(true);
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editingLabel, setEditingLabel] = useState('');

  const tools = [
    { id: 'distance', name: 'Distance', icon: 'Ruler', description: 'Measure distance between two points' },
    { id: 'angle', name: 'Angle', icon: 'Triangle', description: 'Measure angle between three points' },
    { id: 'radius', name: 'Radius', icon: 'Circle', description: 'Measure radius of circular features' },
    { id: 'area', name: 'Area', icon: 'Square', description: 'Calculate surface area' },
    { id: 'perimeter', name: 'Perimeter', icon: 'Hexagon', description: 'Measure perimeter of shapes' },
    { id: 'volume', name: 'Volume', icon: 'Box', description: 'Calculate volume of enclosed spaces' }
  ];

  const units = ['mm', 'cm', 'm', 'in', 'ft'];

  const measurementModes = [
    { id: 'point-to-point', name: 'Point to Point', icon: 'Minus' },
    { id: 'continuous', name: 'Continuous', icon: 'TrendingUp' },
    { id: 'freehand', name: 'Freehand', icon: 'PenTool' }
  ];

  const convertUnit = (value, fromUnit, toUnit) => {
    const conversions = {
      'mm': 1,
      'cm': 10,
      'm': 1000,
      'in': 25.4,
      'ft': 304.8
    };
    const valueInMm = value * conversions?.[fromUnit];
    return valueInMm / conversions?.[toUnit];
  };

  const formatValue = (measurement) => {
    const converted = convertUnit(measurement?.value, 'mm', unit);
    
    switch (measurement?.type) {
      case 'distance': case'perimeter':
        return `${converted?.toFixed(2)} ${unit}`;
      case 'angle':
        return `${measurement?.value?.toFixed(1)}°`;
      case 'area':
        return `${converted?.toFixed(2)} ${unit}²`;
      case 'volume':
        return `${converted?.toFixed(2)} ${unit}³`;
      case 'radius':
        return `R ${converted?.toFixed(2)} ${unit}`;
      default:
        return `${converted?.toFixed(2)} ${unit}`;
    }
  };

  const handleAddMeasurement = () => {
    if (!activeTool) return;

    const newMeasurement = {
      id: Date.now(),
      type: activeTool,
      value: Math.random() * 1000,
      unit: unit,
      mode: measurementMode,
      points: [],
      timestamp: Date.now(),
      color: getRandomColor()
    };

    onAddMeasurement?.(newMeasurement);
  };

  const getRandomColor = () => {
    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
    return colors?.[Math.floor(Math.random() * colors?.length)];
  };

  const handleDeleteMeasurement = (measurementId) => {
    const newMeasurements = measurements?.filter(m => m?.id !== measurementId);
    onClearMeasurements();
    newMeasurements?.forEach(m => onAddMeasurement?.(m));
  };

  const handleExportMeasurements = () => {
    const exportData = measurements?.map(m => ({
      type: m?.type,
      value: formatValue(m),
      timestamp: m?.timestamp && !isNaN(new Date(m?.timestamp)?.getTime()) 
        ? new Date(m?.timestamp)?.toISOString() 
        : new Date()?.toISOString(),
      mode: m?.mode
    }));

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `measurements-${Date.now()}.json`;
    link?.click();
    URL.revokeObjectURL(url);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="absolute bottom-20 right-96 z-25 w-80 bg-white/[0.88] backdrop-blur-md rounded-lg border border-border shadow-floating max-h-[calc(100vh-12rem)] overflow-hidden flex flex-col">
      {/* Header */}
      <div className="flex flex-col border-b border-border bg-card/50">
        <div className="flex items-center justify-between p-4 pb-2">
          <div className="flex items-center space-x-2">
            <Icon name="Ruler" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Measurement Tools</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onToggle}
            className="w-8 h-8"
          />
        </div>
        <div className="px-4 pb-4 pt-1">
          <div className="p-2.5 bg-amber-500/10 border border-amber-500/20 rounded text-[11px] text-amber-500 font-medium leading-relaxed">
            <span className="font-bold block mb-0.5">⚠️ Experimental Stage Only!</span>
            Measurements might not be accurate. Please refer to the 'Specs' for reference and verification while we validate this product. You can leave feedback in the Annotations located next to the Ruler.
          </div>
        </div>
      </div>

      {/* Settings Panel */}
      <div className="p-4 border-b border-border bg-card/30 space-y-3">
        {/* Unit Selector */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">Unit</label>
          <div className="flex space-x-1">
            {units?.map((u) => (
              <button
                key={u}
                onClick={() => setUnit(u)}
                className={`flex-1 px-3 py-1.5 rounded text-sm font-medium transition-smooth ${
                  unit === u
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {u}
              </button>
            ))}
          </div>
        </div>

        {/* Measurement Mode */}
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">Measurement Mode</label>
          <div className="grid grid-cols-3 gap-1">
            {measurementModes?.map((mode) => (
              <button
                key={mode?.id}
                onClick={() => setMeasurementMode(mode?.id)}
                className={`p-2 rounded text-xs font-medium transition-smooth flex flex-col items-center ${
                  measurementMode === mode?.id
                    ? 'bg-primary/20 border border-primary text-primary' :'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                <Icon name={mode?.icon} size={16} className="mb-1" />
                {mode?.name}
              </button>
            ))}
          </div>
        </div>

        {/* Options */}
        <div className="flex items-center justify-between">
          <label className="text-sm text-foreground flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={snapToVertex}
              onChange={(e) => setSnapToVertex(e?.target?.checked)}
              className="rounded border-border"
            />
            <span>Snap to Vertex</span>
          </label>
          <label className="text-sm text-foreground flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showAnnotations}
              onChange={(e) => setShowAnnotations(e?.target?.checked)}
              className="rounded border-border"
            />
            <span>Show Labels</span>
          </label>
        </div>
      </div>

      {/* Measurement Tools */}
      <div className="p-4 border-b border-border">
        <label className="text-sm font-medium text-foreground mb-2 block">Tool</label>
        <div className="grid grid-cols-3 gap-2">
          {tools?.map((tool) => (
            <button
              key={tool?.id}
              onClick={() => setActiveTool(tool?.id)}
              className={`p-3 rounded-lg border transition-smooth ${
                activeTool === tool?.id
                  ? 'bg-primary/10 border-primary' :'bg-muted border-border hover:bg-muted/80'
              }`}
            >
              <Icon 
                name={tool?.icon} 
                size={20} 
                className={`mx-auto mb-1 ${activeTool === tool?.id ? 'text-primary' : 'text-muted-foreground'}`} 
              />
              <p className={`text-xs font-medium text-center ${
                activeTool === tool?.id ? 'text-foreground' : 'text-muted-foreground'
              }`}>
                {tool?.name}
              </p>
            </button>
          ))}
        </div>

        {activeTool && (
          <div className="mt-3 p-3 bg-muted rounded-lg">
            <p className="text-xs text-muted-foreground mb-2">
              {tools?.find(t => t?.id === activeTool)?.description}
            </p>
            {activeTool === 'distance' || activeTool === 'angle' || activeTool === 'radius' || activeTool === 'area' ? (
              <div className="p-2 border border-dashed border-sky-500/30 rounded bg-sky-500/5 text-center">
                <p className="text-[10px] font-bold text-sky-500 uppercase tracking-wider mb-1">Interactive Tool</p>
                <p className="text-xs text-slate-500 leading-normal">
                  {activeTool === 'distance' && "Click on any two points on the 3D model to measure the point-to-point distance."}
                  {activeTool === 'angle' && "Click on any three points on the 3D model to measure the angle."}
                  {activeTool === 'radius' && "Click on any three points on a circular edge or surface to measure its radius."}
                  {activeTool === 'area' && "Click on three or more points on a surface, then click the floating green 'Complete' badge to compute the surface area."}
                </p>
              </div>
            ) : (
              <Button
                variant="primary"
                size="sm"
                onClick={handleAddMeasurement}
                className="w-full"
                iconName="Plus"
              >
                Add Measurement
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Measurements List */}
      <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 32rem)' }}>
        {measurements?.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="Ruler" size={48} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No measurements yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Select a tool and click on the model to start measuring
            </p>
          </div>
        ) : (
          <div className="p-3 space-y-2">
            {measurements?.map((measurement, index) => (
              <div 
                key={measurement?.id || index} 
                onClick={() => onSelectMeasurement?.(selectedMeasurementId === measurement?.id ? null : measurement?.id)}
                className={`p-3 bg-muted rounded-lg hover:bg-muted/80 transition-smooth border-l-4 cursor-pointer ${
                  selectedMeasurementId === measurement?.id ? 'ring-2 ring-primary bg-primary/5 border-l-primary' : ''
                }`}
                style={{ borderLeftColor: selectedMeasurementId === measurement?.id ? undefined : (measurement?.color || '#3B82F6') }}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2 flex-1">
                    <Icon 
                      name={tools?.find(t => t?.id === measurement?.type)?.icon || 'Ruler'} 
                      size={16} 
                      className="text-primary flex-shrink-0" 
                    />
                    <div className="flex-1 min-w-0">
                      {editingId === measurement?.id ? (
                        <div className="flex items-center gap-1.5 mt-1 w-full">
                          <input
                            type="text"
                            value={editingLabel}
                            onChange={(e) => setEditingLabel(e.target.value)}
                            className="flex-1 px-2 py-0.5 border rounded text-xs text-foreground bg-background focus:ring-1 focus:ring-primary outline-none"
                            placeholder="e.g. Length A"
                            autoFocus
                          />
                          <button
                            onClick={() => {
                              const updatedMeasurements = measurements.map(m => 
                                m.id === measurement.id ? { ...m, label: editingLabel } : m
                              );
                              onClearMeasurements();
                              updatedMeasurements.forEach(m => onAddMeasurement(m));
                              setEditingId(null);
                            }}
                            className="p-1 bg-green-500 hover:bg-green-600 text-white rounded transition-colors"
                          >
                            <Icon name="Check" size={10} />
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="p-1 bg-slate-300 hover:bg-slate-400 text-slate-700 rounded transition-colors"
                          >
                            <Icon name="X" size={10} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <span className="text-sm font-medium text-foreground capitalize block">
                            {measurement?.label || measurement?.type}
                          </span>
                          <span className="text-xs text-muted-foreground block">
                            {measurement?.mode?.replace('-', ' ') || 'Point to Point'}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <button 
                    onClick={() => handleDeleteMeasurement(measurement?.id)}
                    className="text-muted-foreground hover:text-destructive transition-smooth p-1"
                  >
                    <Icon name="Trash2" size={14} />
                  </button>
                </div>
                
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-2xl font-bold text-primary">
                      {formatValue(measurement)}
                    </p>
                    {measurement?.points && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {measurement?.points?.length} points
                      </p>
                    )}
                  </div>
                  <div className="flex space-x-1">
                    <button 
                      onClick={() => {
                        setEditingId(measurement?.id);
                        setEditingLabel(measurement?.label || '');
                      }}
                      className="p-1 hover:bg-muted-foreground/20 rounded transition-smooth" 
                      title="Edit Label"
                    >
                      <Icon name="Edit2" size={14} className="text-muted-foreground" />
                    </button>
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText(formatValue(measurement));
                      }}
                      className="p-1 hover:bg-muted-foreground/20 rounded transition-smooth" 
                      title="Copy Value"
                    >
                      <Icon name="Copy" size={14} className="text-muted-foreground" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      {measurements?.length > 0 && (
        <div className="p-4 border-t border-border bg-card/30">
          <div className="flex space-x-2">
            <Button 
              variant="outline" 
              size="sm" 
              iconName="Download" 
              className="flex-1"
              onClick={handleExportMeasurements}
            >
              Export
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              iconName="Trash2" 
              className="flex-1"
              onClick={onClearMeasurements}
            >
              Clear All
            </Button>
          </div>
          <div className="mt-2 text-xs text-center text-muted-foreground">
            {measurements?.length} measurement{measurements?.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}
    </div>
  );
};

export default MeasurementTools;